/**
 * The one place CSA talks to storage — local, remote, or both.
 *
 * Screens import this module and nothing else. That was the whole point of
 * building the capture UI against a seam: when offline capture landed, the
 * screens did not change. Every export below kept its name and its shape.
 *
 * What changed is underneath:
 *
 *   csaApi.js        this facade — what the screens see
 *     └ offline/csaRepo.js   decides local vs remote, owns the outbox
 *         ├ offline/csaDb.js       IndexedDB
 *         ├ offline/outboxRules.js  the queueing rules (unit-tested)
 *         └ offline/net.js          HTTP
 *
 * Offline is opt-in per audit. Until an audit is provisioned, every call here
 * goes straight to the server exactly as it did before — so writing up on a
 * laptop behaves as it always has, and nothing accumulates a local database
 * that nobody asked for. See csaRepo.js for why.
 */

import * as net from "./offline/net";
import * as repo from "./offline/csaRepo";

export { CsaError } from "./offline/net";
export { newLocalId } from "./offline/outboxRules";

/* ------------------------------------------------------------------ */
/* clients — always online; you cannot book an audit from a plant floor */
/* ------------------------------------------------------------------ */

export const listOrganizations = net.listOrganizations;
export const listSites = net.listSites;
export const createOrganization = net.createOrganization;
export const createSite = net.createSite;

/* ------------------------------------------------------------------ */
/* the audit                                                           */
/* ------------------------------------------------------------------ */

export const listAudits = net.listAudits;
export const previewDiscard = net.previewDiscard;
export const discardAudit = net.discardAudit;
export const createAudit = net.createAudit;

export const getFramework = repo.getFramework;
export const getAudit = repo.getAudit;
export const clearFrameworkCache = net.clearFrameworkCache;

/**
 * Scores are the server's business.
 *
 * Deliberately not computed locally. The scoring engine reads a config and a
 * weight table that live server-side, and a second implementation on the client
 * would eventually disagree with the first — which would mean a tablet showing
 * one number and the report showing another. Offline, the capture screen shows
 * progress instead, which is the number that tells an auditor what to do next.
 */
export const getRollup = net.getRollup;
export const getReadiness = net.getReadiness;

/* ------------------------------------------------------------------ */
/* capture — local-first once provisioned                              */
/* ------------------------------------------------------------------ */

export const saveAnswer = repo.saveAnswer;
export const bulkYes = repo.bulkYes;

export const addExclusion = repo.addExclusion;
export const removeExclusion = repo.removeExclusion;

export const listNotes = repo.listNotes;
export const addNote = repo.addNote;
export const useNote = repo.useNote;
export const deleteNote = repo.deleteNote;

export const listExtras = repo.listExtras;
export const addExtra = repo.addExtra;
export const updateExtra = repo.updateExtra;
export const deleteExtra = repo.deleteExtra;

export const getGaps = net.getGaps;

/* ------------------------------------------------------------------ */
/* carry-forward (design §8)                                           */
/* ------------------------------------------------------------------ */

/*
 * Straight to the network, like the client lookups above: a repeat visit is
 * set up at a desk, not on a plant floor. See net.js for why there is no
 * local path.
 */
/* ------------------------------------------------------------------ */
/* trend (design §12 item 11)                                          */
/* ------------------------------------------------------------------ */

export const auditInsight = net.auditInsight;
export const listPeople = net.listPeople;
/* Admin only; the server refuses anyone else regardless of what the UI shows. */
export const adminFramework = net.adminFramework;
export const adminFrameworkTemplate = net.adminFrameworkTemplate;
export const adminFrameworkUpload = net.adminFrameworkUpload;
/* scheduling (§7d) */
export const scheduleConflicts = net.scheduleConflicts;
export const scheduleFor = net.scheduleFor;
export const scheduleLoad = net.scheduleLoad;
export const assignAudit = net.assignAudit;

export const adminSaveConfig = net.adminSaveConfig;
export const adminFrameworkPending = net.adminFrameworkPending;
export const adminFrameworkActivate = net.adminFrameworkActivate;
export const adminFrameworkDiscard = net.adminFrameworkDiscard;
export const adminUsers = net.adminUsers;
export const adminSetRoles = net.adminSetRoles;

export const trendSites = net.trendSites;
export const trend = net.trend;

export const carryPreview = net.carryPreview;
export const carryForward = net.carryForward;
export const confirmCarried = net.confirmCarried;
export const previousAnswers = net.previousAnswers;

/* ------------------------------------------------------------------ */
/* photos (design §9)                                                  */
/* ------------------------------------------------------------------ */

export const capturePhoto = repo.capturePhoto;
export const listPhotos = repo.listPhotos;
export const linkPhoto = repo.linkPhoto;
export const unlinkPhoto = repo.unlinkPhoto;
export const deletePhoto = repo.deletePhoto;
export const uploadPhotos = repo.uploadPhotos;
export const unassignedPhotos = repo.unassignedPhotos;
export const storageEstimate = repo.storageEstimate;
export const syncAndFreeSpace = repo.syncAndFreeSpace;

/* ------------------------------------------------------------------ */
/* workflow — online only                                              */
/* ------------------------------------------------------------------ */

/**
 * Submit materialises 163 observations and freezes the scoring snapshot, so it
 * cannot happen against a local copy. The UI blocks it until the outbox is
 * empty (design §9, Lifecycle).
 */
/*
 * The report is composed on the server and needs the frozen snapshot, so these
 * are online-only — there is nothing to compose from a local copy.
 */
export const getReportModel = net.getReportModel;
export const getReportContents = net.getReportContents;
export const setReportContents = net.setReportContents;
export const saveReportSection = net.saveReportSection;

export const openReportIssue = net.openReportIssue;
export const listReportIssues = net.listReportIssues;
export const getReportIssue = net.getReportIssue;

export const submitAudit = net.submitAudit;
export const reviewAudit = net.reviewAudit;
export const returnAudit = net.returnAudit;
export const reopenAudit = net.reopenAudit;

/* ------------------------------------------------------------------ */
/* offline (design §9)                                                 */
/* ------------------------------------------------------------------ */

export const provision = repo.provision;
export const unprovision = repo.unprovision;
export const isProvisioned = repo.isProvisioned;

export const flush = repo.flush;
export const resync = repo.resync;
export const pending = repo.pending;
export const discard = repo.discard;

export const subscribeSync = repo.subscribe;
export const watchConnection = repo.watchConnection;
export const isOnline = repo.isOnline;

/* Tier 2 drafting (§11). Online only — drafting needs the server. */
export const startDraft = net.startDraft;
export const draftProgress = net.draftProgress;
export const cancelDraft = net.cancelDraft;
export const draftItems = net.draftItems;
export const acceptDraft = net.acceptDraft;

/*
 * Tier 3 capture assist (§8). Online only, and never queued — see net.assist.
 * The screen hides the control when offline rather than letting it fail.
 */
export const assist = net.assist;

/* One question over time (§10c) — the only readers of comparable_to_prior. */
export const trendQuestions = net.trendQuestions;
export const questionTrend = net.questionTrend;

/** A photograph's bytes, served by the API rather than fetched from S3. */
export const photoBytes = net.photoBytes;
